import React from 'react';
import { useActivityItemPricingStepStyles } from './ActivityItemPricingStyles';
import { useGetClientsQuery } from 'features/clients/clientSlice';
import { useGetVendorsQuery } from 'features/vendor/vendorSlice';
import { useGetLotsQuery } from 'features/lots/lotSlice';
import { useGetVehicleTypesQuery } from 'features/vehicles/vehicleSlice';
import LoadingSpinner from 'components/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_ACTIVITY_PRICING_STEP_KEYS } from '../createActivityAndPricingConsts';
import AddPricing from './AddPricing';
import { saveStepData } from '../createActivityAndPricingStateSlice';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const ActivityItemPricingStep = () => {
    const classes = useActivityItemPricingStepStyles();
    const dispatch = useDispatch();
    const pricingDataFromStore = useSelector(
        state => state.createActivityAndPricingState.steps[CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING]
    );
    const pricingData = pricingDataFromStore || {};
    const isEditMode = useSelector(state => state.createActivityAndPricingState.isEditMode);

    const activityDetails = useSelector(
        state => state.createActivityAndPricingState.steps[CREATE_ACTIVITY_PRICING_STEP_KEYS.ACTIVITY_DETAILS]
    );
    const pricingType = activityDetails?.pricingType;
    const materials = activityDetails?.materials || [];

    const { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(null);
    const { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
    const { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
    const { data: vehicleTypes, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
    const editingPricingKeys = pricingData.editingPricingKeys || [];

    const pricingItems = pricingData.pricingItems || [];

    const setPricingItems = (items) => {
        if (pricingData.pricingItems === items) {
            return;
        }

        dispatch(saveStepData({
            stepKey: CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING,
            data: {
                pricingItems: items
            }
        }));
    };

    const onPricingUnsavedChanges = (pricingKey, hasUnsaved) => {
        const nextEditingPricingKeys = hasUnsaved
            ? (editingPricingKeys.includes(pricingKey) ? editingPricingKeys : [...editingPricingKeys, pricingKey])
            : editingPricingKeys.filter(key => key !== pricingKey);

        const somePricingHasUnsavedEdits = nextEditingPricingKeys.length > 0;

        if (
            pricingData.somePricingHasUnsavedEdits === somePricingHasUnsavedEdits
            && pricingData.editingPricingKeys === nextEditingPricingKeys
        ) {
            return;
        }

        dispatch(saveStepData({
            stepKey: CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING,
            data: {
                editingPricingKeys: nextEditingPricingKeys,
                somePricingHasUnsavedEdits
            }
        }));
    };

    const isLoading = isLoadingClients || isLoadingVendors || isLoadingLots || isLoadingVehicleTypes;

    if (isLoading) {
        return <LoadingSpinner loading={true} />;
    }

    return (
        <div className={classes.container}>
            <div className={classes.sectionHeader}>
                <h3>Pricing Configuration</h3>
                {!isEditMode && (
                    <div className={classes.defaultNoteBanner}>
                        <InfoOutlinedIcon className={classes.defaultNoteIcon} />
                        <span className={classes.defaultNoteText}>
                            The first price configured for every lot / vendor / vehicle type combination will apply to all clients.
                        </span>
                    </div>
                )}
                <p>Add pricing configurations for this activity.</p>
            </div>
            <AddPricing
                pricingItems={pricingItems}
                setPricingItems={setPricingItems}
                onUnsavedChanges={onPricingUnsavedChanges}
                lots={lots}
                clients={clients}
                vendors={vendors}
                vehicleTypes={vehicleTypes}
                pricingType={pricingType}
                materials={materials}
            />
        </div>
    );
};

export default ActivityItemPricingStep;
